import React from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Form, Formik } from "formik";
import { createUser, getUserById, updateUserById } from "app/services/api/users";

const normalizeUserDetail = (data) => {
  if (!data) return null;
  // Accept common shapes: {user}, {data}, or the user object directly.
  if (data?.user && typeof data.user === "object") return data.user;
  if (data?.data && typeof data.data === "object") return data.data;
  return typeof data === "object" ? data : null;
};

const resolveUserId = (u) =>
  u?._id ?? u?.id ?? u?.userId ?? u?.user_id ?? u?.uid ?? null;

const toTrimmed = (v) => String(v ?? "").trim();
const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val || "").trim());
const normalizeStatus01 = (raw, fallback = 1) => {
  if (raw === null || raw === undefined || raw === "") return fallback;
  if (typeof raw === "boolean") return raw ? 1 : 0;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw === 0 ? 0 : 1;
  const n = Number(String(raw).trim());
  if (Number.isFinite(n)) return n === 0 ? 0 : 1;
  return fallback;
};

const toInitialValues = (u, isCreate) => {
  if (isCreate) {
    return {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      userType: "3",
      status: 1,
    };
  }
  const username = u?.username ?? u?.userName ?? u?.login ?? "";
  const email = u?.email ?? u?.mail ?? "";
  const firstName = u?.first_name ?? u?.firstName ?? "";
  const lastName = u?.last_name ?? u?.lastName ?? "";
  const typ = u?.typ ?? u?.type ?? u?.userType ?? "";
  const status = normalizeStatus01(u?.status ?? u?.state, 1);
  return {
    username: String(username ?? ""),
    email: String(email ?? ""),
    firstName: String(firstName ?? ""),
    lastName: String(lastName ?? ""),
    userType: typ !== null && typ !== undefined && typ !== "" ? String(typ) : "3",
    status,
  };
};

const parseApiMessage = (e) => {
  const data = e?.response?.data;
  const msg =
    (typeof data === "string" ? data : null) ||
    data?.message ||
    data?.error ||
    data?.detail ||
    e?.message ||
    "";
  return String(msg || "").trim();
};

const looksLikeUsernameConflict = (msg) => {
  const m = String(msg || "").toLowerCase();
  return (
    m.includes("username") &&
    (m.includes("exist") || m.includes("taken") || m.includes("duplicate") || m.includes("unique"))
  );
};

const looksLikeEmailConflict = (msg) => {
  const m = String(msg || "").toLowerCase();
  return (
    m.includes("email") &&
    (m.includes("exist") || m.includes("taken") || m.includes("duplicate") || m.includes("unique"))
  );
};

const DSOKUserForm = () => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const navigate = useNavigate();
  const { userId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const isCreate = String(userId || "") === "new";

  const [loading, setLoading] = React.useState(!isCreate);
  const [error, setError] = React.useState(null);
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    if (isCreate) return;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getUserById(userId);
        if (!alive) return;
        setUser(normalizeUserDetail(data));
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [isCreate, userId]);

  const title = isCreate
    ? t("pages.dsokUsers.form.createTitle", { defaultValue: "Add user" })
    : t("pages.dsokUsers.form.editTitle", { defaultValue: "Edit user" });

  const safeId = resolveUserId(user);
  const status = error?.response?.status;
  const isForbidden = status === 401 || status === 403;
  const isNotFound = status === 404;

  const validate = React.useCallback(
    (values) => {
      const requiredMsg = t("pages.dsokUsers.form.validation.required", {
        defaultValue: "Required field.",
      });
      const invalidEmailMsg = t("pages.dsokUsers.form.validation.invalidEmail", {
        defaultValue: "Invalid email address.",
      });
      const errors = {};
      if (!toTrimmed(values.username)) errors.username = requiredMsg;
      if (!toTrimmed(values.email)) errors.email = requiredMsg;
      else if (!isValidEmail(values.email)) errors.email = invalidEmailMsg;
      if (!toTrimmed(values.firstName)) errors.firstName = requiredMsg;
      if (!toTrimmed(values.lastName)) errors.lastName = requiredMsg;
      if (!toTrimmed(values.userType)) errors.userType = requiredMsg;
      if (![0, 1].includes(Number(values.status))) errors.status = requiredMsg;
      const allowed = new Set(["3", "4", "5", "6"]);
      if (values.userType && !allowed.has(String(values.userType))) {
        errors.userType = requiredMsg;
      }
      return errors;
    },
    [t]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link to="/customer/dsok/users">
          {t("pages.dsokUsers.title", { defaultValue: "Users" })}
        </Link>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/customer/dsok/users")}
        >
          {t("buttons.back", { defaultValue: "Back" })}
        </Button>
        <Typography variant="h4">{title}</Typography>
      </Stack>

      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            {t("common.loading", { defaultValue: "Loading..." })}
          </Typography>
        </Box>
      ) : error ? (
        <Card sx={{ p: 2 }}>
          <Alert severity="error">
            {isForbidden
              ? t("pages.dsokUsers.form.forbidden", {
                  defaultValue: "You don't have permission to view this user.",
                })
              : isNotFound
                ? t("pages.dsokUsers.form.notFound", {
                    defaultValue: "User not found.",
                  })
                : t("pages.dsokUsers.form.loadError", {
                    defaultValue: "Failed to load user detail.",
                  })}
          </Alert>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate("/customer/dsok/users")}>
              {t("buttons.back", { defaultValue: "Back" })}
            </Button>
          </Stack>
        </Card>
      ) : (
        <Formik
          enableReinitialize
          initialValues={toInitialValues(user, isCreate)}
          validate={validate}
          validateOnBlur
          validateOnChange={false}
          onSubmit={async (values, { setSubmitting, setFieldError }) => {
            const payload = {
              username: toTrimmed(values.username),
              email: toTrimmed(values.email),
              first_name: toTrimmed(values.firstName),
              last_name: toTrimmed(values.lastName),
              typ: Number(values.userType),
              status: Number(values.status),
            };

            setSubmitting(true);
            try {
              if (isCreate) {
                await createUser(payload);
                enqueueSnackbar(
                  t("pages.dsokUsers.form.saveSuccessCreate", {
                    defaultValue: "User has been created.",
                  }),
                  { variant: "success" }
                );
              } else {
                await updateUserById(userId, payload);
                enqueueSnackbar(
                  t("pages.dsokUsers.form.saveSuccessUpdate", {
                    defaultValue: "User has been saved.",
                  }),
                  { variant: "success" }
                );
              }
              navigate("/customer/dsok/users");
            } catch (e) {
              console.error(e);
              const msg = parseApiMessage(e);
              const st = e?.response?.status;

              if (st === 409 && looksLikeUsernameConflict(msg)) {
                setFieldError(
                  "username",
                  t("pages.dsokUsers.form.errors.usernameTaken", {
                    defaultValue: "This username is already taken.",
                  })
                );
              } else if (st === 409 && looksLikeEmailConflict(msg)) {
                setFieldError(
                  "email",
                  t("pages.dsokUsers.form.errors.emailTaken", {
                    defaultValue: "This email is already used.",
                  }),
                );
              } else {
                enqueueSnackbar(
                  t("pages.dsokUsers.form.saveError", {
                    defaultValue: "Failed to save user.",
                  }),
                  { variant: "error" }
                );
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            submitCount,
          }) => {
            const showErr = (name) =>
              Boolean(errors?.[name] && (touched?.[name] || submitCount > 0));

            return (
              <Form noValidate>
                <Card sx={{ p: 2 }}>
                  {!isCreate ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t("pages.dsokUsers.form.editingId", {
                        id: safeId != null ? String(safeId) : "",
                        defaultValue:
                          safeId != null ? `Editing user ID: ${String(safeId)}` : "Editing user",
                      })}
                    </Alert>
                  ) : null}

                  <Stack spacing={2}>
                    <TextField
                      name="username"
                      label={t("pages.dsokUsers.form.fields.username", {
                        defaultValue: "Username",
                      })}
                      value={values.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={showErr("username")}
                      helperText={showErr("username") ? errors.username : " "}
                      fullWidth
                      required
                      size="small"
                      autoComplete="off"
                      inputProps={{ maxLength: 128 }}
                    />

                    <TextField
                      name="email"
                      label={t("pages.dsokUsers.form.fields.email", {
                        defaultValue: "Email",
                      })}
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={showErr("email")}
                      helperText={showErr("email") ? errors.email : " "}
                      fullWidth
                      required
                      disabled={!isCreate}
                      size="small"
                      autoComplete="off"
                      inputProps={{ maxLength: 254 }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={Number(values.status) === 1}
                          disabled={isSubmitting}
                          onChange={(_e, checked) =>
                            setFieldValue("status", checked ? 1 : 0, false)
                          }
                        />
                      }
                      label={
                        Number(values.status) === 1
                          ? t("pages.dsokUsers.status.active", { defaultValue: "Active" })
                          : t("pages.dsokUsers.status.inactive", { defaultValue: "Inactive" })
                      }
                    />
                    {showErr("status") ? (
                      <Typography variant="caption" color="error">
                        {errors.status}
                      </Typography>
                    ) : null}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        name="firstName"
                        label={t("pages.dsokUsers.form.fields.firstName", {
                          defaultValue: "First name",
                        })}
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showErr("firstName")}
                        helperText={showErr("firstName") ? errors.firstName : " "}
                        fullWidth
                        required
                        size="small"
                        autoComplete="off"
                        inputProps={{ maxLength: 128 }}
                      />
                      <TextField
                        name="lastName"
                        label={t("pages.dsokUsers.form.fields.lastName", {
                          defaultValue: "Last name",
                        })}
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showErr("lastName")}
                        helperText={showErr("lastName") ? errors.lastName : " "}
                        fullWidth
                        required
                        size="small"
                        autoComplete="off"
                        inputProps={{ maxLength: 128 }}
                      />
                    </Stack>

                    <TextField
                      select
                      name="userType"
                      label={t("pages.dsokUsers.form.fields.userType", {
                        defaultValue: "User type",
                      })}
                      value={values.userType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={showErr("userType")}
                      helperText={showErr("userType") ? errors.userType : " "}
                      fullWidth
                      required
                      size="small"
                    >
                      <MenuItem value="3">
                        {t("pages.dsokUsers.form.userTypes.3", {
                          defaultValue: "Normal user",
                        })}
                      </MenuItem>
                      <MenuItem value="4">
                        {t("pages.dsokUsers.form.userTypes.4", {
                          defaultValue: "Waiter",
                        })}
                      </MenuItem>
                      <MenuItem value="5">
                        {t("pages.dsokUsers.form.userTypes.5", {
                          defaultValue: "Operator easyPay",
                        })}
                      </MenuItem>
                      <MenuItem value="6">
                        {t("pages.dsokUsers.form.userTypes.6", {
                          defaultValue: "Access System operator",
                        })}
                      </MenuItem>
                    </TextField>

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/customer/dsok/users")}
                        disabled={isSubmitting}
                      >
                        {t("buttons.cancel", { defaultValue: "Cancel" })}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                      >
                        {t("buttons.save", { defaultValue: "Save" })}
                      </Button>
                    </Stack>
                  </Stack>
                </Card>
              </Form>
            );
          }}
        </Formik>
      )}
    </Box>
  );
};

export default DSOKUserForm;

